import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Slider,
  Input
} from '@heroui/react';
import { 
  Upload, 
  RotateCcw, 
  RotateCw, 
  Save, 
  X,
  Image as ImageIcon
} from 'lucide-react';
import AvatarEditor from 'react-avatar-editor';

const ImageUploader = ({ isOpen, onClose, onImageSave }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [editorHeight, setEditorHeight] = useState(300);
  const [isDragging, setIsDragging] = useState(false);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  // Debug: Check if file input ref is properly set
  useEffect(() => {
    console.log('ImageUploader mounted, fileInputRef:', fileInputRef.current);
  }, []);

  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened, fileInputRef:', fileInputRef.current);
    }
  }, [isOpen]);

  const handleFileSelect = useCallback((file) => {
    console.log('handleFileSelect called with:', file);
    if (file && file.type.startsWith('image/')) {
      console.log('Valid image file, setting state');
      setSelectedFile(file);
      setScale(1.0);
      setRotation(0);
    } else {
      console.log('Invalid file type or no file');
    }
  }, []);

  const handleFileChange = (e) => {
    console.log('File input change event triggered');
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', file.name, file.type, file.size);
      handleFileSelect(file);
    } else {
      console.log('No file selected');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRotate = (direction) => {
    setRotation(prev => prev + (direction === 'left' ? -90 : 90));
  };

  const handleSave = () => {
    if (editorRef.current && selectedFile) {
      const canvas = editorRef.current.getImageScaledToCanvas();
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create a file-like object with the blob
          const imageData = {
            blob,
            canvas,
            dataURL: canvas.toDataURL('image/png'),
            file: selectedFile
          };
          
          onImageSave(imageData);
          handleClose();
        }
      }, 'image/png', 0.9);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setScale(1.0);
    setRotation(0);
    setEditorHeight(300);
    onClose();
  };

  const handleEditorLoad = (info) => {
    // Adjust editor height based on image aspect ratio
    const maxHeight = 400;
    const minHeight = 200;
    const aspectRatio = info.width / info.height;
    
    let newHeight;
    if (aspectRatio > 1.5) {
      // Wide image
      newHeight = Math.max(minHeight, 500 / aspectRatio);
    } else if (aspectRatio < 0.7) {
      // Tall image
      newHeight = Math.min(maxHeight, 500 / aspectRatio);
    } else {
      // Square-ish image
      newHeight = 300;
    }
    
    setEditorHeight(Math.round(newHeight));
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      backdrop="opaque"
      classNames={{
        base: "max-h-[90vh] max-w-2xl",
        backdrop: "bg-black/50",
        wrapper: "flex items-center justify-center",
        body: "p-0 bg-white",
        header: "bg-white border-b border-gray-200",
        footer: "bg-white border-t border-gray-200"
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center space-x-2 px-6 py-4">
          <ImageIcon className="w-5 h-5 text-primary" />
          <span>Upload e Editar Imagem</span>
        </ModalHeader>
        
        <ModalBody className="px-6">
          {!selectedFile ? (
            // File selection area
            <div className="space-y-4">
              <div 
                className={`border-2 border-dashed rounded-lg transition-colors cursor-pointer p-12 ${
                  isDragging 
                    ? 'border-primary bg-primary-50' 
                    : 'border-gray-300 hover:border-primary hover:bg-gray-50'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Click detected on drop area');
                  console.log('fileInputRef.current:', fileInputRef.current);
                  
                  if (fileInputRef.current) {
                    console.log('Triggering file input click');
                    try {
                      fileInputRef.current.click();
                    } catch (error) {
                      console.error('Error clicking file input:', error);
                    }
                  } else {
                    console.error('fileInputRef.current is null');
                  }
                }}
              >
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <Upload className="w-12 h-12 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-700">
                      Clique para selecionar ou arraste uma imagem
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Formatos suportados: JPG, PNG, GIF (máx. 10MB)
                    </p>
                  </div>
                </div>
              </div>
              
              <input
                key="file-input-main"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            // Image editor
            <div className="space-y-4">
              <Card>
                <CardBody className="p-4">
                  <div className="flex justify-center">
                    <AvatarEditor
                      ref={editorRef}
                      image={selectedFile}
                      width={500}
                      height={editorHeight}
                      border={10}
                      color={[255, 255, 255, 0.8]}
                      scale={scale}
                      rotate={rotation}
                      onLoadSuccess={handleEditorLoad}
                      className="rounded-lg"
                    />
                  </div>
                </CardBody>
              </Card>
              
              {/* Controls */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zoom: {Math.round(scale * 100)}%
                  </label>
                  <Slider
                    size="sm"
                    step={0.1}
                    minValue={0.5}
                    maxValue={3.0}
                    value={scale}
                    onChange={setScale}
                    className="w-full"
                    color="primary"
                  />
                </div>
                
                <div className="flex items-center justify-center space-x-2">
                  <Button
                    size="sm"
                    variant="bordered"
                    startContent={<RotateCcw className="w-4 h-4" />}
                    onPress={() => handleRotate('left')}
                  >
                    Girar ←
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="bordered"
                    startContent={<RotateCw className="w-4 h-4" />}
                    onPress={() => handleRotate('right')}
                  >
                    Girar →
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="light"
                    onPress={(e) => {
                      console.log('Change image button clicked');
                      console.log('fileInputRef.current:', fileInputRef.current);
                      
                      if (fileInputRef.current) {
                        try {
                          fileInputRef.current.click();
                        } catch (error) {
                          console.error('Error clicking file input from button:', error);
                        }
                      } else {
                        console.error('fileInputRef.current is null in button handler');
                      }
                    }}
                    startContent={<Upload className="w-4 h-4" />}
                  >
                    Trocar Imagem
                  </Button>
                </div>
              </div>
              
              <input
                key="file-input-editor"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </ModalBody>
        
        <ModalFooter className="px-6 py-4">
          <Button
            variant="light"
            onPress={handleClose}
            startContent={<X className="w-4 h-4" />}
          >
            Cancelar
          </Button>
          
          {selectedFile && (
            <Button
              color="primary"
              onPress={handleSave}
              startContent={<Save className="w-4 h-4" />}
            >
              Salvar Imagem
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImageUploader;
